import React, {useEffect, useRef, useState} from 'react';
import {useModule} from '@/hooks/useModule';
import {DBObjects} from '@/store/store_types';
import {Button, Select, Slider, Box, Group} from '@mantine/core';
import {IconPlayerPlay, IconPlayerPause, IconPlayerStop} from '@tabler/icons-react';
import {parseMidi, MidiData} from 'midi-file';
import {Soundfont} from 'smplr';

type MidiNote = {
    noteNumber: number;
    startTime: number;
    duration: number;
    velocity: number;
    track: number;
};

type ParsedMidiData = {
    notes: MidiNote[];
    duration: number;
    tempo: number;
    timeSignature: {numerator: number; denominator: number};
};

type InstrumentName =
    | 'acoustic_grand_piano'
    | 'bright_acoustic_piano'
    | 'electric_grand_piano'
    | 'electric_piano_1'
    | 'electric_piano_2'
    | 'harpsichord'
    | 'clavinet'
    | 'church_organ'
    | 'drawbar_organ'
    | 'rock_organ'
    | 'violin'
    | 'viola'
    | 'cello'
    | 'string_ensemble_1'
    | 'synth_strings_1';

const CANVAS_HEIGHT = 400;
const KEYS_WIDTH = 60;
const NOTE_HEIGHT = 8;
const PIXELS_PER_SECOND = 100;
const MIN_NOTE = 21;
const MAX_NOTE = 108;

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

const getNoteColor = (track: number) => {
    const colors = [
        '#3498db',
        '#2ecc71',
        '#e74c3c',
        '#f39c12',
        '#9b59b6',
        '#1abc9c',
        '#e67e22',
        '#34495e',
    ];
    return colors[track % colors.length];
};

const parseMidiFile = (arrayBuffer: ArrayBuffer): ParsedMidiData => {
    const midiData = parseMidi(new Uint8Array(arrayBuffer)) as MidiData;
    const notes: MidiNote[] = [];
    const ticksPerBeat = midiData.header.ticksPerBeat || 480;
    let tempo = 500000;
    let timeSignature = {numerator: 4, denominator: 4};

    const microsecondsPerTick = tempo / ticksPerBeat;

    midiData.tracks.forEach((track, trackIndex) => {
        let currentTime = 0;
        const activeNotes = new Map<number, {startTime: number; velocity: number}>();

        track.forEach(event => {
            currentTime += event.deltaTime;

            if (event.type === 'setTempo') {
                tempo = event.microsecondsPerBeat;
            }

            if (event.type === 'timeSignature') {
                timeSignature = {
                    numerator: event.numerator,
                    denominator: event.denominator,
                };
            }

            if (event.type === 'noteOn' && event.velocity > 0) {
                activeNotes.set(event.noteNumber, {
                    startTime: currentTime,
                    velocity: event.velocity,
                });
            }

            if (event.type === 'noteOff' || (event.type === 'noteOn' && event.velocity === 0)) {
                const noteData = activeNotes.get(event.noteNumber);
                if (noteData) {
                    const startTimeSeconds = (noteData.startTime * microsecondsPerTick) / 1000000;
                    const endTimeSeconds = (currentTime * microsecondsPerTick) / 1000000;

                    notes.push({
                        noteNumber: event.noteNumber,
                        startTime: startTimeSeconds,
                        duration: endTimeSeconds - startTimeSeconds,
                        velocity: noteData.velocity,
                        track: trackIndex,
                    });

                    activeNotes.delete(event.noteNumber);
                }
            }
        });
    });

    const maxTime = Math.max(...notes.map(n => n.startTime + n.duration), 1);

    return {
        notes,
        duration: maxTime,
        tempo,
        timeSignature,
    };
};

type MidiViewerProps = {
    fileInfo: DBObjects['File'];
};

export const MidiViewer = ({fileInfo}: MidiViewerProps) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [midiData, setMidiData] = useState<ParsedMidiData | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
    const [soundfontPlayer, setSoundfontPlayer] = useState<Soundfont | null>(null);
    const [instrument, setInstrument] = useState<InstrumentName>('acoustic_grand_piano');
    const [isLoadingInstrument, setIsLoadingInstrument] = useState(false);
    const [activeNotes, setActiveNotes] = useState<Set<number>>(new Set());
    const [tempoMultiplier, setTempoMultiplier] = useState(1.0);
    const playbackTimerRef = useRef<number | null>(null);
    const playbackStartTimeRef = useRef<number>(0);
    const pausedAtRef = useRef<number>(0);
    const scheduledNotesRef = useRef<Set<string>>(new Set());
    const activeNotesRef = useRef<Map<string, () => void>>(new Map());

    const fileClient = useModule('MSFiles').client();

    useEffect(() => {
        const loadMidi = async () => {
            const src = await fileClient.getFileSrc(fileInfo);
            const response = await fetch(src);
            const arrayBuffer = await response.arrayBuffer();
            const parsed = parseMidiFile(arrayBuffer);
            setMidiData(parsed);
        };

        loadMidi();
    }, [fileInfo]);

    useEffect(() => {
        const loadInstrument = async () => {
            if (soundfontPlayer) {
                soundfontPlayer.stop();
            }

            setIsLoadingInstrument(true);
            const ctx = audioContext || new AudioContext();
            if (!audioContext) {
                setAudioContext(ctx);
            }

            const player = new Soundfont(ctx, { instrument });
            await player.load;
            setSoundfontPlayer(player);
            setIsLoadingInstrument(false);
        };

        loadInstrument();

        return () => {
            if (soundfontPlayer) {
                soundfontPlayer.stop();
            }
        };
    }, [instrument]);

    useEffect(() => {
        if (!midiData || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const containerWidth = containerRef.current?.clientWidth || 800;
        canvas.width = Math.max(containerWidth, midiData.duration * PIXELS_PER_SECOND + KEYS_WIDTH);
        canvas.height = CANVAS_HEIGHT;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const noteRange = MAX_NOTE - MIN_NOTE;
        const totalHeight = CANVAS_HEIGHT;

        for (let note = MIN_NOTE; note <= MAX_NOTE; note++) {
            const y = ((MAX_NOTE - note) / noteRange) * totalHeight;
            const isBlackKey = [1, 3, 6, 8, 10].includes(note % 12);
            const isActive = activeNotes.has(note);

            ctx.fillStyle = isBlackKey ? '#2a2a2a' : '#3a3a3a';
            ctx.fillRect(KEYS_WIDTH, y, canvas.width - KEYS_WIDTH, NOTE_HEIGHT);

            if (isActive) {
                ctx.fillStyle = isBlackKey ? '#ff4444' : '#ff6666';
            } else {
                ctx.fillStyle = isBlackKey ? '#000' : '#fff';
            }
            ctx.fillRect(0, y, KEYS_WIDTH, NOTE_HEIGHT);

            if (isActive) {
                ctx.strokeStyle = '#ff0000';
                ctx.lineWidth = 2;
                ctx.strokeRect(1, y + 1, KEYS_WIDTH - 2, NOTE_HEIGHT - 2);
            }

            if (note % 12 === 0) {
                ctx.fillStyle = isActive ? '#fff' : '#888';
                ctx.font = '10px Arial';
                const noteName = `${NOTE_NAMES[note % 12]}${Math.floor(note / 12) - 1}`;
                ctx.fillText(noteName, 5, y + NOTE_HEIGHT - 2);
            }
        }

        midiData.notes.forEach(note => {
            if (note.noteNumber < MIN_NOTE || note.noteNumber > MAX_NOTE) return;

            const x = KEYS_WIDTH + note.startTime * PIXELS_PER_SECOND;
            const y = ((MAX_NOTE - note.noteNumber) / noteRange) * totalHeight;
            const width = Math.max(note.duration * PIXELS_PER_SECOND, 2);

            const alpha = note.velocity / 127;
            ctx.fillStyle = getNoteColor(note.track);
            ctx.globalAlpha = alpha * 0.8 + 0.2;
            ctx.fillRect(x, y, width, NOTE_HEIGHT);
            ctx.globalAlpha = 1;

            ctx.strokeStyle = '#000';
            ctx.lineWidth = 0.5;
            ctx.strokeRect(x, y, width, NOTE_HEIGHT);
        });

        if (isPlaying || currentTime > 0) {
            const playheadX = KEYS_WIDTH + currentTime * PIXELS_PER_SECOND;
            ctx.strokeStyle = '#ff0000';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(playheadX, 0);
            ctx.lineTo(playheadX, canvas.height);
            ctx.stroke();
        }

    }, [midiData, currentTime, activeNotes]);

    const playNote = (noteNumber: number, velocity: number, duration: number) => {
        if (!soundfontPlayer) return;

        const noteKey = `${noteNumber}-${Date.now()}`;

        setActiveNotes(prev => new Set(prev).add(noteNumber));

        const stopNote = soundfontPlayer.start({
            note: noteNumber,
            velocity,
            duration,
        });

        if (stopNote) {
            activeNotesRef.current.set(noteKey, stopNote);

            setTimeout(() => {
                activeNotesRef.current.delete(noteKey);
                setActiveNotes(prev => {
                    const next = new Set(prev);
                    next.delete(noteNumber);
                    return next;
                });
            }, duration * 1000);
        }
    };

    const handlePlay = () => {
        if (!midiData || !soundfontPlayer) return;

        if (audioContext && audioContext.state === 'suspended') {
            audioContext.resume();
        }

        setIsPlaying(true);
        playbackStartTimeRef.current = Date.now();
        pausedAtRef.current = currentTime;
        scheduledNotesRef.current.clear();

        const animate = () => {
            const elapsed = ((Date.now() - playbackStartTimeRef.current) / 1000) * tempoMultiplier;
            const newTime = pausedAtRef.current + elapsed;

            if (newTime >= midiData.duration) {
                handleStop();
                return;
            }

            setCurrentTime(newTime);

            const lookahead = 0.2;
            midiData.notes.forEach(note => {
                const noteKey = `${note.noteNumber}-${note.startTime}`;
                if (
                    note.startTime >= newTime &&
                    note.startTime < newTime + lookahead &&
                    !scheduledNotesRef.current.has(noteKey)
                ) {
                    const delay = (note.startTime - newTime) / tempoMultiplier;
                    const adjustedDuration = note.duration / tempoMultiplier;
                    setTimeout(() => {
                        playNote(note.noteNumber, note.velocity, adjustedDuration);
                    }, delay * 1000);
                    scheduledNotesRef.current.add(noteKey);
                }
            });

            playbackTimerRef.current = requestAnimationFrame(animate);
        };

        animate();
    };

    const handlePause = () => {
        setIsPlaying(false);
        if (playbackTimerRef.current) {
            cancelAnimationFrame(playbackTimerRef.current);
            playbackTimerRef.current = null;
        }
        pausedAtRef.current = currentTime;
    };

    const handleStop = () => {
        setIsPlaying(false);
        if (playbackTimerRef.current) {
            cancelAnimationFrame(playbackTimerRef.current);
            playbackTimerRef.current = null;
        }
        setCurrentTime(0);
        pausedAtRef.current = 0;
        scheduledNotesRef.current.clear();

        activeNotesRef.current.forEach(stopNote => {
            if (stopNote) {
                stopNote();
            }
        });
        activeNotesRef.current.clear();
        setActiveNotes(new Set());

        if (soundfontPlayer) {
            soundfontPlayer.stop();
        }
    };

    const handleSeek = (value: number) => {
        const newTime = (value / 100) * (midiData?.duration || 0);
        setCurrentTime(newTime);
        pausedAtRef.current = newTime;
        scheduledNotesRef.current.clear();

        if (isPlaying) {
            playbackStartTimeRef.current = Date.now();
        }
    };

    const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!midiData || !canvasRef.current) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const clickedTime = (x - KEYS_WIDTH) / PIXELS_PER_SECOND;

        if (clickedTime >= 0 && clickedTime <= midiData.duration) {
            setCurrentTime(clickedTime);
            pausedAtRef.current = clickedTime;
            scheduledNotesRef.current.clear();

            if (isPlaying) {
                playbackStartTimeRef.current = Date.now();
            }
        }
    };

    useEffect(() => {
        return () => {
            if (playbackTimerRef.current) {
                cancelAnimationFrame(playbackTimerRef.current);
            }

            activeNotesRef.current.forEach(stopNote => {
                try {
                    if (stopNote) {
                        stopNote();
                    }
                } catch(e) {
                    console.error('failed to stop note: ' + e);
                }
            });
            activeNotesRef.current.clear();

            if (soundfontPlayer) {
                soundfontPlayer.stop();
            }

            if (audioContext) {
                audioContext.close();
            }
        };
    }, []);

    if (!midiData) {
        return <div>Loading MIDI file...</div>;
    }

    if (isLoadingInstrument && !soundfontPlayer) {
        return <div>Loading instrument...</div>;
    }

    const progress = midiData.duration > 0 ? (currentTime / midiData.duration) * 100 : 0;

    return (
        <Box>
            <Group mb="md">
                <Button
                    onClick={isPlaying ? handlePause : handlePlay}
                    leftSection={isPlaying ? <IconPlayerPause size={16} /> : <IconPlayerPlay size={16} />}
                >
                    {isPlaying ? 'Pause' : 'Play'}
                </Button>
                <Button
                    onClick={handleStop}
                    leftSection={<IconPlayerStop size={16} />}
                >
                    Stop
                </Button>
                <Select
                    label="Instrument"
                    value={instrument}
                    onChange={(value) => value && setInstrument(value as InstrumentName)}
                    data={[
                        {value: 'acoustic_grand_piano', label: 'Grand Piano'},
                        {value: 'bright_acoustic_piano', label: 'Bright Piano'},
                        {value: 'electric_grand_piano', label: 'Electric Grand'},
                        {value: 'electric_piano_1', label: 'Electric Piano'},
                        {value: 'electric_piano_2', label: 'Electric Piano 2'},
                        {value: 'harpsichord', label: 'Harpsichord'},
                        {value: 'clavinet', label: 'Clavinet'},
                        {value: 'church_organ', label: 'Church Organ'},
                        {value: 'drawbar_organ', label: 'Drawbar Organ'},
                        {value: 'rock_organ', label: 'Rock Organ'},
                        {value: 'violin', label: 'Violin'},
                        {value: 'viola', label: 'Viola'},
                        {value: 'cello', label: 'Cello'},
                        {value: 'string_ensemble_1', label: 'String Ensemble'},
                        {value: 'synth_strings_1', label: 'Synth Strings'},
                    ]}
                    style={{width: '180px'}}
                    disabled={isPlaying || isLoadingInstrument}
                />
                <div style={{width: '150px'}}>
                    <div style={{fontSize: '12px', fontWeight: 500, marginBottom: '4px'}}>
                        Tempo: {tempoMultiplier.toFixed(1)}x
                    </div>
                    <Slider
                        value={tempoMultiplier}
                        onChange={setTempoMultiplier}
                        min={0.25}
                        max={2.0}
                        step={0.05}
                        marks={[
                            {value: 0.5, label: '0.5x'},
                            {value: 1.0, label: '1x'},
                            {value: 1.5, label: '1.5x'},
                            {value: 2.0, label: '2x'},
                        ]}
                        disabled={isPlaying}
                    />
                </div>
                <div style={{flex: 1}}>
                    <Slider
                        value={progress}
                        onChange={handleSeek}
                        label={value => {
                            const time = (value / 100) * midiData.duration;
                            const minutes = Math.floor(time / 60);
                            const seconds = Math.floor(time % 60);
                            return `${minutes}:${seconds.toString().padStart(2, '0')}`;
                        }}
                    />
                </div>
                <div style={{minWidth: '60px', textAlign: 'right'}}>
                    {Math.floor(currentTime / 60)}:{Math.floor(currentTime % 60).toString().padStart(2, '0')} / {Math.floor(midiData.duration / 60)}:{Math.floor(midiData.duration % 60).toString().padStart(2, '0')}
                </div>
            </Group>

            <div
                ref={containerRef}
                style={{
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    border: '1px solid #444',
                    borderRadius: '4px',
                }}
            >
                <canvas
                    ref={canvasRef}
                    onClick={handleCanvasClick}
                    style={{
                        display: 'block',
                        cursor: 'pointer',
                    }}
                />
            </div>

            <div style={{marginTop: '10px', fontSize: '12px', color: '#888'}}>
                Tempo: {Math.round(60000000 / midiData.tempo)} BPM |
                Time Signature: {midiData.timeSignature.numerator}/{midiData.timeSignature.denominator} |
                Notes: {midiData.notes.length}
            </div>
        </Box>
    );
};
